import React, { useState } from 'react';

function Step3({ formData, onInputChange, onSubmit, themes, submitLoading, disabled = false }) {
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const CHECK_API_ENDPOINT = process.env.REACT_APP_FIELD_VALIDATION_API;

  const checkEmailExists = async (email) => {
    if (!email || !isValidEmail(email)) {
      setEmailStatus(null);
      return;
    }

    setCheckingEmail(true);
    setEmailStatus(null);

    try {
      const response = await fetch(CHECK_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'email',
          value: email.toLowerCase().trim()
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setEmailStatus({
          exists: data.exists,
          message: data.message
        });

        if (!data.exists) {
          setTimeout(() => {
            setEmailStatus(null);
          }, 5000);
        }
      } else {
        setEmailStatus({
          exists: false,
          message: 'Error checking email. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailStatus({
        exists: false,
        message: 'Error checking email. Please try again.'
      });
    } finally {
      setCheckingEmail(false);
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // newly phone validation function option 1: basic regex validation
  // const isValidPhone = (phone) => {
  //   // Remove all non-digit characters to count actual digits
  //   const digitsOnly = phone.replace(/\D/g, '');
    
  //   // Check if it has between 10-15 digits
  //   if (digitsOnly.length < 10 || digitsOnly.length > 15) {
  //     return false;
  //   }
    
  //   // Allow only digits, +, -, (, ), and spaces
  //   const phoneRegex = /^[0-9+\-() ]+$/;
  //   return phoneRegex.test(phone);
  // };


  // newly phone validation function option 2: more strict regex validation
  const isValidPhone = (phone) => {
    // Remove all whitespace for validation
    const trimmedPhone = phone.trim();
    
    // Must not be empty
    if (!trimmedPhone) return false;
    
    // Regex pattern:
    // - Optional + at the start (for country code)
    // - Must start with a digit after optional +
    // - Can contain digits, spaces, hyphens, and parentheses
    // - Parentheses must be balanced and contain digits
    // - No special characters at the end
    const phoneRegex = /^\+?[\d][\d\s\-()]*[\d]$/;
    
    // Check basic pattern
    if (!phoneRegex.test(trimmedPhone)) {
      return false;
    }
    
    // Check parentheses are balanced
    const openParens = (trimmedPhone.match(/\(/g) || []).length;
    const closeParens = (trimmedPhone.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      return false;
    }
    
    // Extract only digits
    const digitsOnly = trimmedPhone.replace(/\D/g, '');
    
    // Must have 10-15 digits
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return false;
    }
    
    // + can only appear at the start
    const plusCount = (trimmedPhone.match(/\+/g) || []).length;
    if (plusCount > 1 || (plusCount === 1 && trimmedPhone[0] !== '+')) {
      return false;
    }
    
    return true;
  };

  // newly phone validation handler -basic
  // const handlePhoneChange = (value) => {
  //   // Only allow valid characters while typing
  //   const sanitizedValue = value.replace(/[^0-9+\-() ]/g, '');
  //   onInputChange('phone', sanitizedValue);
  // };

  // handle phone change with more strict validation
  const handlePhoneChange = (value) => {
    let sanitizedValue = value;
    
    // Remove any character that's not a digit, +, -, (, ), or space
    sanitizedValue = sanitizedValue.replace(/[^0-9+\-() ]/g, '');
    
    // Ensure + only appears at the start
    if (sanitizedValue.includes('+')) {
      const firstPlus = sanitizedValue.indexOf('+');
      sanitizedValue = '+' + sanitizedValue.replace(/\+/g, '');
      if (firstPlus > 0) {
        sanitizedValue = sanitizedValue.replace('+', '');
      }
    }
    
    // Limit to 20 characters total (including formatting)
    sanitizedValue = sanitizedValue.slice(0, 20);
    
    onInputChange('phone', sanitizedValue);
  };

  const handleEmailChange = (value) => {
    onInputChange('email', value);
    setEmailStatus(null);

    // Clear previous timeout
    if (window.emailCheckTimeout) {
      clearTimeout(window.emailCheckTimeout);
    }

    // Only check if email format is valid
    if (value && isValidEmail(value)) {
      // Debounce: wait 800ms after user stops typing
      window.emailCheckTimeout = setTimeout(() => {
        checkEmailExists(value);
      }, 800);
    }
  };

  // Handle submit with email validation
  const handleSubmitClick = () => {

    if (disabled) {
      alert('Trial limit reached. Cannot create more websites.');
      return;
    }

    // Check if email exists
    if (emailStatus && emailStatus.exists) {
      alert('This email is already registered. Please use a different email.');
      return;
    }

    // Proceed with original submit
    onSubmit();
  };

  return (
    <div className="step-card step-3">
      <div className="step-header-inline">
        <span className="step-number green">3</span>
        <div>
          <h2 className="step-heading">Ad free trial</h2>
          <p className="step-subtitle">Start with premium features at no cost.</p>
        </div>
      </div>
      
      <div className="step-body">
        <div className="form-group">
          <label className="form-label">Selected Theme</label>
          <select 
            className="form-control"
            value={formData.selectedTheme}
            onChange={(e) => onInputChange('selectedTheme', e.target.value)}
            disabled={disabled || submitLoading}
          >
            <option value="">Choose your theme...</option>
            {themes && themes.length > 0 ? (
              themes.map((theme) => (
                <option key={theme.id} value={theme.id}>
                  {theme.icon} {theme.name}
                </option>
              ))
            ) : (
              <option value="" disabled>Loading themes...</option>
            )}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Email Address</label>
          <input
            type="email"
            className="form-control"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => handleEmailChange(e.target.value)}
            disabled={disabled || submitLoading}
            required
          />
          
          {/* Email Status */}
          {checkingEmail && (
            <div className="availability-status checking">
              <span className="spinner-tiny"></span>
              <span>Checking email...</span>
            </div>
          )}
          
          {emailStatus && !emailStatus.exists && (
            <div className="availability-status available">
              <span className="icon-check">✓</span>
              <span>Email available</span>
            </div>
          )}
          
          {emailStatus && emailStatus.exists && (
            <div className="availability-status unavailable">
              <span className="icon-cross">✗</span>
              <span>{emailStatus.message}</span>
            </div>
          )}
        </div>
        
        {/* without phone validation */}
        {/* <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            className="form-control"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={(e) => onInputChange('phone', e.target.value)}
            disabled={disabled || submitLoading}
            required
          />
        </div> */}

        {/* with phone validation */}
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            className="form-control"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            disabled={disabled || submitLoading}
            required
          />
          
          {/* Phone validation message */}
          {formData.phone && !isValidPhone(formData.phone) && (
            <div className="availability-status unavailable">
              <span className="icon-cross">✗</span>
              <span>Invalid phone number format</span>
            </div>
          )}
        </div>

        <div className="offer-box">
          <div className="offer-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M3 7.5l4.5 3.5L12 4.5l4.5 6.5L21 7.5l-1.8 10H4.8L3 7.5z" />
              <rect x="4.8" y="18.6" width="14.4" height="1.9" rx="0.9" />
            </svg>
          </div>
          <div className="offer-text">
            <div className="offer-title">4 Week Free Ad-Free Trial</div>
            <div className="offer-note">Enjoy all premium features with no credit card required. Cancel anytime.</div>
          </div>
        </div>

        <button 
          className="btn-custom btn-start-trial" 
          onClick={handleSubmitClick}
          disabled={disabled || submitLoading || (emailStatus && emailStatus.exists) || !formData.phone || !isValidPhone(formData.phone)}
        >
          {submitLoading ? (
            <>
              <span className="spinner-small"></span>
              Creating Website...
            </>
          ) : disabled ? (
            <>
              <span className="icon">🚫</span>
              Trial Limit Reached
            </>
          ) : (
            <>
              <span className="icon">⚡</span>
              Create Website
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default Step3;